import { useState, type ReactNode } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HistoryIcon from "@mui/icons-material/History";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  Outlet,
  Link as RouterLink,
  useLocation,
  useParams,
} from "react-router-dom";
import type { Promisable } from "type-fest";

import type {
  Assessment,
  Person,
  PersonInvolvement,
} from "@thalamos/haven-common";

import { apiHooks } from "../../api/index.js";
import { AssessmentActivity } from "../../components/AssessmentActivity/AssessmentActivity.js";
import { AssessmentKindChip } from "../../components/AssessmentKindChip/AssessmentKindChip.js";
import { AssessmentStatusChip } from "../../components/AssessmentStatusChip/AssessmentStatusChip.js";
import { PersonBanner } from "../../components/PersonBanner/PersonBanner.js";
import { createRoute } from "../../routing/createRoute.js";
import { VISUALLY_HIDDEN } from "../../theme/visuallyHidden.js";
import { AssessmentProvider, useAssessment } from "./AssessmentContext.js";
import { assessmentReference } from "./assessmentReference.js";

/** Why an assessment could not be read, which is all its layout then shows. */
export type AssessmentReadFailure = "not_found" | "unavailable";

/**
 * The frame around every screen of one assessment: whose it is, which it is
 * and what state it is in, with the way back to the young person's profile.
 *
 * Holds the assessment open in an `AssessmentProvider`, keyed by its id, for
 * the screens inside it to share.
 */
export const AssessmentLayout = () => {
  const { assessmentId } = useParams();
  const location: { state: unknown } = useLocation();

  // `useParams` types every segment as optional, but the route cannot match
  // without this one.
  const requestedId = assessmentId ?? "";

  return (
    <AssessmentProvider
      key={requestedId}
      requestedId={requestedId}
      carryOverAsked={carryOverAskedIn(location.state)}
    >
      <AssessmentLayoutFor />
    </AssessmentProvider>
  );
};

/**
 * Whether the start that led here asked to carry the earlier assessment's
 * ratings over. Read from navigation state, which anything could have put
 * there, so only an explicit `true` counts.
 */
export const carryOverAskedIn = (state: unknown): boolean =>
  typeof state === "object" &&
  state !== null &&
  "carryOver" in state &&
  state.carryOver === true;

const AssessmentLayoutFor = () => {
  const { assessment, opening } = useAssessment();

  if (assessment === undefined) {
    return (
      <AssessmentLayoutContent
        assessment={undefined}
        failure={
          opening.kind === "failed" && opening.reason !== "not_open"
            ? opening.reason
            : undefined
        }
        person={undefined}
        involvements={undefined}
      >
        <Outlet />
      </AssessmentLayoutContent>
    );
  }

  return <AssessmentLayoutAbout assessment={assessment} />;
};

/** Every act on one assessment, over whichever of its screens is open. */
const AssessmentActivityDialog = ({
  assessment,
  onClose,
}: {
  assessment: Assessment;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  // The whole screen on a phone, so the log gets the width it needs.
  const narrow = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm" fullScreen={narrow}>
      <DialogContent>
        <AssessmentActivity assessment={assessment} />
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>
          {t("common.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** The layout once the assessment is read, and so whose it is known. */
const AssessmentLayoutAbout = ({ assessment }: { assessment: Assessment }) => {
  const [{ data: person, response: personResponse }] = apiHooks.person.byId(
    assessment.personId,
  );
  const [{ data: involvements, response: involvementsResponse }] =
    apiHooks.involvement.forPerson(assessment.personId);
  const [showingActivity, setShowingActivity] = useState(false);

  return (
    <>
      <AssessmentLayoutContent
        assessment={assessment}
        person={personResponse?.status === 200 ? person : undefined}
        involvements={
          involvementsResponse?.status === 200 ? involvements : undefined
        }
        onShowActivity={() => {
          setShowingActivity(true);
        }}
      >
        <Outlet />
      </AssessmentLayoutContent>
      {showingActivity ? (
        <AssessmentActivityDialog
          assessment={assessment}
          onClose={() => {
            setShowingActivity(false);
          }}
        />
      ) : null}
    </>
  );
};

/**
 * The layout's rendering, with everything it shows injected so a story or a
 * test can show any state of it without an API client.
 *
 * Until the assessment is read nothing of it shows, and an assessment that
 * cannot be read shows only why: someone who may not open it learns nothing
 * about whose it is from the frame around it. One whose answers were withheld
 * shows the frame and says so in place of the screen, which would otherwise
 * show it unanswered.
 */
export const AssessmentLayoutContent = ({
  assessment,
  failure,
  person,
  involvements,
  onShowActivity,
  children,
}: {
  assessment: Assessment | undefined;
  failure?: AssessmentReadFailure | undefined;
  /** The young person, for the banner; it is left out until read. */
  person: Person | undefined;
  /** Who is involved with them, for the banner. */
  involvements: PersonInvolvement[] | undefined;
  /** Shows every act on the assessment; absent where the log is not offered. */
  onShowActivity?: (() => Promisable<void>) | undefined;
  /** The screen inside the layout. */
  children: ReactNode;
}) => {
  const { t } = useTranslation();

  if (assessment === undefined) {
    return failure === undefined ? (
      <Typography variant="body2" color="text.secondary">
        {t("common.loading")}
      </Typography>
    ) : (
      <Alert severity="error">{t(`assessment.page.failed.${failure}`)}</Alert>
    );
  }

  return (
    <Stack spacing={3}>
      <Box
        sx={{
          position: "sticky",
          top: 0,
          // Above the screen's own controls as they scroll beneath, and below
          // the app's bars, drawers and dialogs.
          zIndex: (theme) => theme.zIndex.appBar - 1,
          bgcolor: "background.default",
        }}
      >
        <Stack spacing={1}>
          {person === undefined ? null : (
            <PersonBanner person={person} involvements={involvements ?? []} />
          )}
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            useFlexGap
            flexWrap="wrap"
          >
            <Button
              component={RouterLink}
              to={createRoute.person(assessment.personId)}
              startIcon={<ArrowBackIcon />}
              size="small"
            >
              {t("assessment.layout.back")}
            </Button>
            <Typography sx={{ fontWeight: 600, color: "primary.main" }}>
              {t("assessment.layout.record")}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                letterSpacing: "0.02em",
                color: "text.secondary",
              }}
            >
              <Box component="span" sx={VISUALLY_HIDDEN}>
                {`${t("assessment.layout.reference")} `}
              </Box>
              {assessmentReference(assessment)}
            </Typography>
            <AssessmentStatusChip status={assessment.status} />
            <AssessmentKindChip definitive={assessment.definitive} />
            {onShowActivity === undefined ? null : (
              <Button
                size="small"
                startIcon={<HistoryIcon />}
                onClick={() => void onShowActivity()}
              >
                {t("personPage.assessments.activity.open")}
              </Button>
            )}
          </Stack>
        </Stack>
      </Box>
      {assessment.answersWithheld === true ? (
        <Alert severity="info">{t("assessment.page.contentsWithheld")}</Alert>
      ) : (
        children
      )}
    </Stack>
  );
};
